import { useState } from 'react';
import { Scale } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface Category {
  id: string;
  label: string;
  desc: string;
  emoji: string;
}

export const CATEGORIES: Category[] = [
  {
    id: 'court',
    label: 'Суд',
    desc: 'Позовні заяви, апеляції, скарги до суду, претензії',
    emoji: '⚖️',
  },
  {
    id: 'employment',
    label: 'Трудові відносини',
    desc: 'Заяви до роботодавця, накази, трудові договори',
    emoji: '💼',
  },
  {
    id: 'contracts',
    label: 'Договори',
    desc: 'Купівля-продаж, оренда, послуги, позика між сторонами',
    emoji: '📋',
  },
  {
    id: 'complaints',
    label: 'Скарга / Звернення',
    desc: 'Звернення до держорганів, прокуратури, омбудсмена',
    emoji: '✉️',
  },
  {
    id: 'realestate',
    label: 'Нерухомість',
    desc: 'Договори щодо нерухомого майна, оренда, купівля-продаж',
    emoji: '🏠',
  },
  {
    id: 'general',
    label: 'Інше',
    desc: 'Будь-який інший юридичний документ',
    emoji: '📄',
  },
];

export function categoryLabel(id: string): string {
  return CATEGORIES.find((c) => c.id === id)?.label ?? id;
}

interface Props {
  onSelect: (category: string) => void;
  loading?: boolean;
}

export function CategoryPicker({ onSelect, loading }: Props) {
  const [hovered, setHovered] = useState<string | null>(null);

  return (
    <div className="flex-1 flex flex-col items-center justify-center px-4 py-8 min-h-0">
      <div className="w-full max-w-2xl">
        {/* AI greeting bubble */}
        <div className="flex gap-3 mb-6">
          <div className="w-8 h-8 rounded-lg bg-blue-600 flex-shrink-0 flex items-center justify-center mt-0.5">
            <Scale className="h-4 w-4 text-white" />
          </div>
          <div className="bg-white/5 border border-white/10 rounded-2xl rounded-tl-sm px-5 py-3.5 text-sm text-white/80 leading-relaxed">
            Вітаю! Я — ваш цифровий юридичний асистент.<br />
            <span className="text-white/50">Оберіть тип документа, щоб я міг підібрати правильний підхід:</span>
          </div>
        </div>

        {/* Category grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 ml-11">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              disabled={loading}
              onClick={() => onSelect(cat.id)}
              onMouseEnter={() => setHovered(cat.id)}
              onMouseLeave={() => setHovered(null)}
              className={cn(
                'group relative flex flex-col items-start gap-1.5 rounded-xl border px-4 py-3.5 text-left transition-all duration-150',
                'border-white/10 bg-white/4 hover:border-blue-500/50 hover:bg-blue-600/10',
                loading && 'opacity-50 cursor-not-allowed',
                hovered === cat.id && 'border-blue-500/50 bg-blue-600/10'
              )}
            >
              <span className="text-xl leading-none">{cat.emoji}</span>
              <span className="text-sm font-medium text-white/90">{cat.label}</span>
              <span className="text-[11px] text-white/40 leading-snug">{cat.desc}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
